import * as wjCore from "@grapecity/wijmo";
import { CollectionViewNavigator } from "@grapecity/wijmo.react.input";
import { CollectionView, DataType, Globalize, Control, SortDescription } from "@grapecity/wijmo";
import { FlexGrid } from "@grapecity/wijmo.react.grid";
import { forwardRef, RefObject, useEffect, useImperativeHandle, useRef, useState } from "react";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import * as wjcGridFilter from "@grapecity/wijmo.grid.filter";
import { Selector } from "@grapecity/wijmo.grid.selector";
import BaseMethods from "AlignBaseUI/BaseMethods";
import { AllowSorting, CellRange } from "@grapecity/wijmo.grid";
import { constant } from "@/Utils/constants";
import { EHelpType, IHelpHeader, loadHelpData } from "@/Redux/Ducks/helpSlice";
import { EApprovalStatusType } from '@/Redux/Ducks/inboxSlice';
import { ArrayKeyValueObject, KeyValueObject } from "@/UI/Interfaces/IBaseUI";
import { FlexGridSearch } from '@grapecity/wijmo.react.grid.search';
import { useAppDispatch, useAppSelector } from "@/Hook/hooks";
import classNames from "classnames";
import { Button } from "react-bootstrap";
import { setActiveObjectId } from "@/Redux/Ducks/entryFormSlice";

interface IProps {
  initializeGrid?: (GridControl: any, FilterControl: any) => void
  id: string;
  columns: Array<IHelpHeader>;
  data: Array<any>;
  multiSelect: boolean;
  paging?: boolean;
  serverPaging?: boolean;
  totalRowsCount?: number;
  filteredRowsCount?: number;
  allSearch?: boolean;
  multiSelectButton?: boolean;
  helpType: EHelpType;
  rowSelect?(selectedRow: any): void;
  beforeCollectionViewInitialize?(option: KeyValueObject): void;
  changePage?(pageSize: number, pageIndex: number): void;
  sortColumn?(orderBy: KeyValueObject): void;
  filterApplied?(filterObject: KeyValueObject): void;
  wholeSearchChange?(value: string, searchParameters: KeyValueObject): void;
  extraParam?: KeyValueObject;
  modalBodyStyle?: React.CSSProperties;
  modalStyle?: React.CSSProperties;
  searchText?: string;
}

export type THelpTableHelpHandle = {
  gridSearchRef: RefObject<any>
  gridRef: RefObject<any>
  gridFilterRef: RefObject<any>
  selectedRowRef: RefObject<any>,
  setPaging: React.Dispatch<React.SetStateAction<TPaging>>
}

type TPaging = {
  totalRows: number;
  filteredRows: number;
  startIndex: number;
  endIndex: number;
  pageSize: number;
  pageIndex: number;
}

const TableHelpUI = forwardRef<THelpTableHelpHandle, IProps>((props, ref) => {
  const dispatch = useAppDispatch();

  const [gridHeader, setGridHeader] = useState<any>([]);
  const [gridRows, setGridRows] = useState<any>();

  const pageSizes = [25, 50, 100];
  const [paging, setPaging] = useState<TPaging>({ totalRows: 0, filteredRows: 0, startIndex: -1, endIndex: -1, pageSize: 25, pageIndex: -1 });
  const serverSideWholeSearchTimeout = useRef<any>(null);
  const openFilterObj = useRef<any>(null);

  // const [lastSelectedRowsOnView, setLastSelectedRowsOnView] = useState([]);
  // const [selectedItems, setSelectedItems] = useState([]);

  const selectedItems = useRef<Array<any>>([]);
  const lastSelectedRowsOnView = useRef<Array<any>>([]);
  const isFirstLoad = useRef<boolean>(true);

  const [isSelectorRefUpdated, setSelectorRefStatus] = useState<boolean>(false);
  const [selectionComplete, setSelectionComplete] = useState(true);

  const [isRefUpdated, setRefStatus] = useState<boolean>(false);

  const dataFormat = useAppSelector(state => state.User.CompanyDataFormat);
  // const activeObjectId = useAppSelector((x) => x.Tab.activeTabKey);

  let gridRef: any = useRef();
  let filterRef: any = useRef();
  let gridSelectorRef: any = useRef();
  let gridSearchRef: any = useRef();

  useEffect(() => {
    setGridHeader(getColumns(props.columns));

    const pageOption: KeyValueObject = {
      pageSize: 25,
      pageIndex: -1
    };

    props.beforeCollectionViewInitialize && props.beforeCollectionViewInitialize(pageOption);

    setPaging((x) => ({
      ...x,
      pageSize: pageOption.pageSize,
      pageIndex: pageOption.pageIndex
    }));

    const collectionViewOption: KeyValueObject = {};

    if (!props.serverPaging && props.paging) {
      collectionViewOption.pageSize = pageOption.pageSize;
    }

    const collectionRow = new CollectionView(props.data, collectionViewOption);
    setGridRows(collectionRow);

  }, [props.columns]);

  // Page Size Change and Pagination Change
  useEffect(() => {
    if (gridRows) {
      if (props.serverPaging) {
        if (isFirstLoad.current) {
          isFirstLoad.current = false;
          props.changePage && props.changePage(paging.pageSize, paging.pageIndex);
          return; // Skip the first execution
        }

        // If Pages are smaller then current page index
        if (paging.pageIndex + 1 > Math.ceil(paging.filteredRows / paging.pageSize))
          setPaging((x) => ({ ...x, pageIndex: Math.ceil(paging.filteredRows / paging.pageSize) - 1 }));
        else {
          dispatch(loadHelpData({ objectId: props.id, filter: { pageIndex: paging.pageIndex, pageSize: paging.pageSize } }, {
            helpLoaded: () => {
              props.changePage && props.changePage(paging.pageSize, paging.pageIndex);
            }
          }));
        }
      }
      else {
        gridRows.pageSize = paging.pageSize;
        props.changePage && props.changePage(paging.pageSize, paging.pageIndex);
      }
    }
  }, [paging.pageSize, paging.pageIndex]);

  useEffect(() => {
    if (gridRows) {
      const data = props.data.map((x) => {
        const row_id = BaseMethods.uniqueIdGenerator(JSON.stringify(x));
        return { ...x, row_id };
      });

      gridRows.sourceCollection = data;
      if (props.paging) {
        let rowsCount = data.length;
        let filteredRows = data.length;
        if (props.serverPaging) {
          rowsCount = props.totalRowsCount as number;
          filteredRows = props.filteredRowsCount as number;
        }

        const pageCount = Math.ceil(filteredRows / paging.pageSize);

        if (pageCount === 1) {
          setPaging((x) => ({
            ...x,
            totalRows: rowsCount,
            filteredRows: filteredRows,
            endIndex: filteredRows,
            startIndex: 1,
            pageIndex: x.pageIndex == -1 ? 0 : x.pageIndex
          }));
        }
        else {
          let pageIndex = paging.pageIndex;
          if (pageIndex == -1 && filteredRows > 0)
            pageIndex = 0;

          let start = paging.pageSize * pageIndex;
          let end = start + paging.pageSize - 1;
          if (end >= filteredRows) {
            end -= (end % filteredRows) + 1;
          }

          setPaging((x) => ({
            ...x,
            totalRows: rowsCount,
            filteredRows: filteredRows,
            endIndex: end + 1,
            startIndex: start + 1,
            pageIndex
          }));

        }
      }

    }

  }, [props.data, isRefUpdated]);

  useEffect(() => {
    if (gridRef.current) {
      const GridControl = gridRef.current.control;

      const gridLoadedRows = (s: any, e: any) => {
        // if (selectionComplete) {
        s.rows.map((x: any) => {
          x.isSelected = !!selectedItems.current.find(
            (y: any) => y.dataItem.row_id == x.dataItem.row_id
          );
        });

        // if (findMatch(lastSelectedRowsOnView.map((x: any) => x.dataItem.row_id), s.rows.map((x: any) => x.dataItem.row_id)).not_match.length)
        lastSelectedRowsOnView.current = s.rows.filter(
          (x: any) => x.isSelected
        );
        // }
      }

      const copyingCell = (s: any, e: any) => {
        e.cancel = true;
        const data = e.panel._activeCell.textContent;
        wjCore.Clipboard.copy(data);
      }

      const cellDblClick = (e: any) => {
        var ht = GridControl.hitTest(e);
        if (ht.row != -1) {
          var ColID = GridControl.columns[ht.col];
          if (!ColID) return;

          if (!props.multiSelectButton)
            return;

          if (props.multiSelect) {
            props.rowSelect?.([GridControl.rows[ht.row].dataItem]);
          } else {
            props.rowSelect?.(GridControl.rows[ht.row].dataItem);
          }
        }
      }

      const sortingColumn = (s: any, e: any) => {

        const column = s.columns[e.col]; // Get sorted column
        const isAscending = !column.currentSort || column.currentSort === "-1";

        const orderByObject = {
          columnId: column.binding,
          direction: isAscending ? "ASC" : "DESC"
        }

        if (props.serverPaging) {
          e.cancel = true;
          dispatch(loadHelpData({ objectId: props.id, filter: { orderBy: [orderByObject] } }, {
            helpLoaded: () => {
              s.itemsSource.sortDescriptions.clear();
              s.itemsSource.sortDescriptions.push(new SortDescription(column.binding, isAscending));
              props.sortColumn && props.sortColumn(orderByObject);
            }
          }));
        }
        else {
          props.sortColumn && props.sortColumn(orderByObject);
        }

      }

      const filterChanged = (s: any, e: any) => {
        if (gridRef.current && !e.cancel) {
          const filterCon = filterRef.current?.control;
          if (!filterCon) return;

          const filter = filterCon.getColumnFilter(e.col);
          var colConfig = props.columns.find((y: any) => y.ColumnID == filter.conditionFilter.column.binding);

          const filterObject = {
            columnId: filter.conditionFilter.column.binding as string,
            active1: filter.conditionFilter.condition1.isActive as boolean,
            operator1: filter.conditionFilter.condition1.operator as number,
            value1: filter.conditionFilter.condition1.value as string,
            and: filter.conditionFilter.and as boolean,
            active2: filter.conditionFilter.condition2.isActive as boolean,
            operator2: filter.conditionFilter.condition2.operator as number,
            value2: filter.conditionFilter.condition2.value as string,
            controltype: colConfig?.controltype as string,
          }

          filterObject.operator1 = filterObject.operator1 == null ? -1 : filterObject.operator1;
          filterObject.operator2 = filterObject.operator2 == null ? -1 : filterObject.operator2;

          if (filterObject.operator1.isEqual(wjcGridFilter.Operator.GT, wjcGridFilter.Operator.GE, wjcGridFilter.Operator.LT, wjcGridFilter.Operator.LE) && !filterObject.value1) {
            filter.conditionFilter.condition1.operator = null;
          }

          if (filterObject.operator2.isEqual(wjcGridFilter.Operator.GT, wjcGridFilter.Operator.GE, wjcGridFilter.Operator.LT, wjcGridFilter.Operator.LE) && !filterObject.value2) {
            filter.conditionFilter.condition2.operator = null;
          }

          if (filter.column.dataType.isEqual(DataType.String) && filterObject.operator1.isEqual(wjcGridFilter.Operator.CT) && !filterObject.value1) {
            filter.conditionFilter.condition1.operator = null;
          }

          if (filter.column.dataType.isEqual(DataType.String) && filterObject.operator2.isEqual(wjcGridFilter.Operator.CT) && !filterObject.value2) {
            filter.conditionFilter.condition2.operator = null;
          }

          if (filter.column.dataType.isEqual(DataType.Date, DataType.Number, DataType.Boolean) && !filterObject.value1) {
            filter.conditionFilter.condition1.operator = null;
          }

          if (filter.column.dataType.isEqual(DataType.Date, DataType.Number, DataType.Boolean) && !filterObject.value2) {
            filter.conditionFilter.condition2.operator = null;
          }

          if (
            filter.conditionFilter.condition1.isActive == openFilterObj.current.active1 &&
            filter.conditionFilter.condition2.isActive == openFilterObj.current.active2 &&

            filter.conditionFilter.condition1.operator == openFilterObj.current.operator1 &&
            filter.conditionFilter.condition2.operator == openFilterObj.current.operator2 &&

            filter.conditionFilter.condition1.value == openFilterObj.current.value1 &&
            filter.conditionFilter.condition2.value == openFilterObj.current.value2 &&

            filter.conditionFilter.and == openFilterObj.current.and
          ) {
            e.cancel = true;
            return;
          }


          if (props.serverPaging) {
            e.cancel = true;

            isFirstLoad.current = true;
            setPaging((x) => ({
              ...x,
              pageIndex: 0
            }));
            dispatch(loadHelpData({ objectId: props.id, filter: { pageIndex: 0, filterCondition: [filterObject] } }, {
              helpLoaded: () => {

                filter.conditionFilter.column.binding = filterObject.columnId;
                filter.conditionFilter.condition1.operator = filterObject.operator1 == -1 ? null : filterObject.operator1;
                filter.conditionFilter.condition1.value = filterObject.value1;
                filter.conditionFilter.and = filterObject.and;
                filter.conditionFilter.condition2.operator = filterObject.operator2 == -1 ? null : filterObject.operator2;
                filter.conditionFilter.condition2.value = filterObject.value2;

                filterCon.apply();

                props.filterApplied && props.filterApplied(filterObject);
              }
            }));

          }
          else {
            isFirstLoad.current = true;
            setPaging((x) => ({
              ...x,
              pageIndex: 0
            }));
            props.filterApplied && props.filterApplied(filterObject);
          }

        }
      }

      const editingFilter = (s: any, e: any) => {
        const colFilter = s.getColumnFilter(e.col);
        openFilterObj.current = {
          columnId: colFilter.conditionFilter.column.binding,
          active1: colFilter.conditionFilter.condition1.isActive,
          operator1: colFilter.conditionFilter.condition1.operator,
          value1: colFilter.conditionFilter.condition1.value,
          and: colFilter.conditionFilter.and,
          active2: colFilter.conditionFilter.condition2.isActive,
          operator2: colFilter.conditionFilter.condition2.operator,
          value2: colFilter.conditionFilter.condition2.value,
        };

        if (colFilter.column.dataType.isEqual(DataType.String)) {
          if (!openFilterObj.current.value1)
            openFilterObj.current.value1 = "";
          if (!openFilterObj.current.value2)
            openFilterObj.current.value2 = "";
        }

        if (colFilter.conditionFilter.condition1.operator == null) {
          if (colFilter.column.dataType.isEqual(DataType.Date, DataType.Number, DataType.Boolean))
            colFilter.conditionFilter.condition1.operator = wjcGridFilter.Operator.EQ;
          else if (colFilter.column.dataType.isEqual(DataType.String))
            colFilter.conditionFilter.condition1.operator = wjcGridFilter.Operator.CT;
        }


        setTimeout(() => {
          const filterEditor = s._divEdt;
          if (filterEditor) {
            const firstInput = filterEditor.querySelector("div[wj-part=div-val1] input");
            if (firstInput) {
              firstInput.focus();
            }
          }
        }, 50);

      }

      const inputChange = (ev: any) => {

        if (props.serverPaging) {
          // to prevent wijmo grid own search
          if (gridSearchRef.current.control._toSearch)
            clearTimeout(gridSearchRef.current.control._toSearch);
        }

        if (serverSideWholeSearchTimeout.current)
          clearTimeout(serverSideWholeSearchTimeout.current);

        serverSideWholeSearchTimeout.current = setTimeout((() => {
          serverSideWholeSearchTimeout.current = null;

          const wholeSearchParameter: ArrayKeyValueObject = [];

          if (props.serverPaging && ev.target.value) {
            GridControl.columns.map((x: any) => {
              if (x.visible) {
                var colConfig = props.columns.find((y: any) => y.ColumnID == x.binding);
                if (colConfig && !colConfig.controltype.isEqual('IMG')) {
                  wholeSearchParameter.push({
                    columnId: colConfig.ColumnID,
                    controlTypeNo: colConfig.controltype
                  });
                }
              }
            });
          }

          props.wholeSearchChange && props.wholeSearchChange(ev.target.value, wholeSearchParameter);
          if (props.serverPaging) {

            dispatch(loadHelpData({ objectId: props.id, filter: { pageIndex: 0, wholeSearchParameter: wholeSearchParameter as any, wholeSearchValue: ev.target.value } }, {
              helpLoaded: () => {
                gridSearchRef.current.control._applySearch();
              }
            }));
          }

        }), gridSearchRef.current.control._delay);

      }

      const clearSearchButton = (ev: any) => {
        props.wholeSearchChange && props.wholeSearchChange("", []);

        dispatch(loadHelpData({ objectId: props.id, filter: { pageIndex: 0, wholeSearchParameter: [], wholeSearchValue: "" } }));
      }

      GridControl.loadedRows.addHandler(gridLoadedRows);
      GridControl.copying.addHandler(copyingCell);
      GridControl.cells.hostElement.addEventListener("dblclick", cellDblClick);
      GridControl.sortingColumn.addHandler(sortingColumn);
      filterRef.current.control.filterChanged.addHandler(filterChanged);
      filterRef.current.control.editingFilter.addHandler(editingFilter);

      if (props.allSearch) {
        gridSearchRef.current.control._tbx.addEventListener("input", inputChange);
        gridSearchRef.current.control._btn.addEventListener("click", clearSearchButton);
      }

      return () => {
        GridControl.loadedRows.removeHandler(gridLoadedRows);
        GridControl.copying.removeHandler(copyingCell);
        GridControl.cells.hostElement.removeEventListener("dblclick", cellDblClick);
        GridControl.sortingColumn.removeHandler(sortingColumn);
        filterRef.current?.control?.filterChanged?.removeHandler?.(filterChanged);
        gridSearchRef?.current?.control?._tbx?.removeEventListener("input", inputChange);
        gridSearchRef?.current?.control?._btn?.removeEventListener("click", clearSearchButton);
      };
    }
  }, [isRefUpdated]);

  useEffect(() => {
    if (gridSelectorRef.current) {
      gridSelectorRef.current.itemChecked.addHandler((s: any, e: any) => {
        const GridControl = gridRef.current.control;

        const currentSelectedRowsOnView = GridControl.rows.filter(
          (x: any) => x.isSelected
        );

        const newSelectedRows = currentSelectedRowsOnView.filter(
          (x: any) =>
            lastSelectedRowsOnView.current.findIndex(
              (y: any) => y.dataItem.row_id == x.dataItem.row_id
            ) == -1
        );

        const DeSelectedRows = lastSelectedRowsOnView.current.filter(
          (x: any) =>
            currentSelectedRowsOnView.findIndex(
              (y: any) => y.dataItem.row_id == x.dataItem.row_id
            ) == -1
        );

        selectedItems.current = selectedItems.current.concat(newSelectedRows);
        selectedItems.current = selectedItems.current.filter(
          (x: any) =>
            DeSelectedRows.findIndex(
              (y: any) => y.dataItem.row_id == x.dataItem.row_id
            ) == -1
        );

        if (props.multiSelect && !props.multiSelectButton)
          props.rowSelect?.(selectedItems.current);

        lastSelectedRowsOnView.current = currentSelectedRowsOnView;
      });

      return () => {
        gridSelectorRef.current.itemChecked.removeHandler();
      };
    }
  }, [isSelectorRefUpdated]);

  useImperativeHandle(ref, () => ({
    gridSearchRef: gridSearchRef,
    gridRef: gridRef,
    gridFilterRef: filterRef,
    selectedRowRef: selectedItems,
    setPaging
  }));

  const getColumns = (columns: Array<IHelpHeader>) => {
    var obj: any = columns.map((x) => {
      const header = x.ColumnText;
      const binding = x.ColumnID;
      let dataType = DataType.String;
      const width = 180;
      let format = null;
      let cssClass = "";
      let visible = binding == "prid" ? false : !x.ishidden;

      if (BaseMethods.getControlType(x.controltype) == "checkbox") {
        cssClass = "switch";
        dataType = DataType.Boolean;
      } else if (BaseMethods.getControlType(x.controltype) == "date") {
        format = dataFormat.find(y => y.fielddataformatno == x.fielddataformatno)?.fielddataformatmasking || constant.DefaultDateFormat;
        dataType = DataType.Date;
      } else if (BaseMethods.getControlType(x.controltype) == "time") {
        format = dataFormat.find(y => y.fielddataformatno == x.fielddataformatno)?.fielddataformatmasking || constant.DefaultTimeFormat;
        dataType = DataType.Date;
      } else if (BaseMethods.getControlType(x.controltype) == "number") {
        format = dataFormat.find(y => y.fielddataformatno == x.fielddataformatno)?.fielddataformatmasking || constant.DefaultNumber;
        dataType = DataType.Number;
      }

      if (x.ColumnID.isEqual("approvalhigheststatus", "approvaltime")) {
        visible = false;
        if (props.helpType.isEqual(EHelpType.DocumentHelp)) {
          if (props.extraParam && props.extraParam.hasApproval == true) {
            visible = true;
          }
        }
      }

      return {
        header,
        binding,
        visible,
        width,
        dataType,
        cssClass,
        format,
      };
    });

    obj.push({
      header: " ",
      binding: "endcol",
      width: "*",
      dataType: DataType.String,
      cssClass: "",
      format: "",
      visible: true,
      allowDragging: false,
      allowSorting: false
    });

    return obj;
  };

  const initGrid = (GridControl: any) => {

    filterRef.current.control.filterColumns = GridControl.columns.filter((x: any) => x.binding != 'endcol' && !props.columns.find((y: any) => y.ColumnID == x.binding)?.controltype?.isEqual('IMG')).map((x: any) => x.binding);
    filterRef.current.control.defaultFilterType = wjcGridFilter.FilterType.Condition;


    if (props.allSearch) {
      let theSearch = gridSearchRef.current.control;
      theSearch.focus();

      theSearch.grid = GridControl;
      theSearch._getItemText = function (item: any) {
        let vals = [],
          cols = this._g.columns;
        for (let i = 0; i < cols.length; i++) {
          let col = cols[i],
            binding = col._binding;
          var colConfig = props.columns.find((x: any) => x.ColumnID == col.binding);

          if (col.visible && binding && !colConfig?.controltype?.isEqual('IMG')) {
            let val = binding.getValue(item);
            if (col.dataMap) {
              val = col.dataMap.getDisplayValue(val);
            }
            else {
              if (col.dataType == DataType.Date)
                val = Globalize.format(val, col.format.replaceAll(" ", ""));
              else
                val = Globalize.format(val, col.format);
            }
            vals.push(val);
          }
        }
        return vals.join(" ");
      };
    }


    if (props.multiSelect) {
      gridSelectorRef.current = new Selector(GridControl);
      setSelectorRefStatus(true);
    }

    GridControl.formatItem.addHandler(function (s: any, e: any) {
      var col = s.columns[e.col];

      if (!col) return;

      if (e.panel == s.cells) {
        var colConfig = props.columns.find((x: any) => x.ColumnID == col.binding);
        var item = s.rows[e.row].dataItem;

        if (col.dataType == DataType.Date) {
          if (e.cell.innerHTML == "1900-01-01T00:00:00") {
            e.cell.innerHTML = "";
          }
          else {
            e.cell.innerHTML = BaseMethods.convertDateToCompanyFormat(item[col.binding], col.format);
          }
        }
        else if (col.binding.isEqual("approvalhigheststatus")) {
          let html;
          if (e.cell.innerHTML == EApprovalStatusType.Pending) {
            html = `<span class="bg-success badge">Approval In Process</span>`
          }
          else if (e.cell.innerHTML == EApprovalStatusType.Approve) {
            html = `<span class="bg-primary badge">Approved</span>`
          }
          else if (e.cell.innerHTML == EApprovalStatusType.Reject) {
            html = `<span class="bg-danger badge">Rejected</span>`
          }
          else if (e.cell.innerHTML == EApprovalStatusType.Hold) {
            html = `<span class="bg-warning badge">Hold</span>`
          }
          else {
            html = `<span class="bg-info badge">Not Initiated</span>`
          }

          e.cell.innerHTML = html
        }
        else if (col.binding.isEqual("approvalstatusno")) {
          let html;
          if (e.cell.innerHTML == EApprovalStatusType.PendingText) {
            html = `<span class="bg-success badge">${e.cell.innerHTML}</span>`
          }
          else if (e.cell.innerHTML == EApprovalStatusType.ApproveText) {
            html = `<span class="bg-primary badge">${e.cell.innerHTML}</span>`
          }
          else if (e.cell.innerHTML == EApprovalStatusType.RejectText) {
            html = `<span class="bg-danger badge">${e.cell.innerHTML}</span>`
          }
          else if (e.cell.innerHTML == EApprovalStatusType.HoldText) {
            html = `<span class="bg-warning badge">${e.cell.innerHTML}</span>`
          }
          else {
            html = `<span class="bg-info badge">${e.cell.innerHTML}</span>`
          }

          e.cell.innerHTML = html
        }
        else if (colConfig?.controltype.isEqual('IMG')) {
          if (e.cell.innerText) {
            var base64String = `data:image/jpeg;base64,${e.cell.innerText}`;
            (e.cell as HTMLElement).style.paddingTop = "0px";
            (e.cell as HTMLElement).style.paddingBottom = "0px";
            (e.cell as HTMLElement).style.textAlign = "center";
            (e.cell as HTMLElement).innerHTML = `<img src="${base64String}" class="rounded-circle thumb-xs me-1" />`;
          }
          else {
            (e.cell as HTMLElement).style.paddingTop = "0px";
            (e.cell as HTMLElement).style.paddingBottom = "0px";
            (e.cell as HTMLElement).style.textAlign = "center";
            (e.cell as HTMLElement).innerHTML = `<img src="${require("@/Assets/Images/file_icon/no-picture.png").default}" class="rounded-circle thumb-xs me-1" />`;
          }
        }
      }
    });

    if (props.initializeGrid)
      props.initializeGrid(GridControl, filterRef.current.control);
  };

  const initSearch = (SearchControl: any) => {
    SearchControl.hostElement.addEventListener("keydown", (ev: any) => {
      let rowCount = SearchControl.grid.rows?.length;
      let colCount = SearchControl.grid.columns?.length;

      if (ev.keyCode == wjCore.Key.Down) {
        ev.preventDefault();

        let firstVisibleRowIndex = -1;
        let firstVisibleColIndex = -1;

        // Find the first visible column
        for (let colIndex = 0; colIndex < colCount; colIndex++) {
          if (SearchControl.grid.columns[colIndex]?.visible) {
            firstVisibleColIndex = colIndex;
            break;
          }
        }

        // Find the first visible row
        for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
          if (SearchControl.grid.rows[rowIndex]?.isVisible) {
            firstVisibleRowIndex = rowIndex;
            break;
          }
        }

        // Ensure both visible row and column exist before setting selection
        if (firstVisibleRowIndex !== -1 && firstVisibleColIndex !== -1) {
          setTimeout(() => {
            SearchControl.grid.selection = new CellRange(firstVisibleRowIndex, firstVisibleColIndex);
            SearchControl.grid.focus();
          }, 0);
        }
      }
    }, true);
  }

  const updateRef = (e: any) => {
    gridRef.current = e;
    setRefStatus(true);
  };

  const selectMultiRow = () => {
    props.rowSelect?.(selectedItems.current.map((x: any) => x.dataItem));
  };

  const filter = (e: any, binding: string) => {
    if (gridRef.current?.control?.collectionView && e.text && e.text != "") {
      let filter = e.text.toLowerCase();
      gridRef.current.control.collectionView.filter = (item: any) => {
        return (
          filter.length == 0 || item[binding].toLowerCase().indexOf(filter) > -1
        );
      };
    } else {
      gridRef.current.control.collectionView.filter = null;
    }
  };

  const onPageSizeChange = (e: any) => {
    setPaging((x) => ({ ...x, pageSize: Number(e.target.value) }));
  }

  const modalBodyStyle = Object.assign({}, props.modalBodyStyle) || {};
  const modalStyle = Object.assign({}, props.modalStyle) || {};

  if (!modalStyle.height)
    modalStyle.height = "100%";

  if (!modalBodyStyle.height)
    modalBodyStyle.height = "100%";

  if (props.multiSelect)
    modalBodyStyle.height = `calc(${modalBodyStyle.height} - 58px)`;

  if (gridHeader.length)
    return (
      <>
        <div style={modalStyle}>
          <div className="modal-body" style={modalBodyStyle}>
            {
              props.allSearch ?
                <div className="search-help">
                  <FlexGridSearch
                    delay={800}
                    ref={gridSearchRef}
                    initialized={initSearch}
                    placeholder="Search"
                  />
                </div>
                : null
            }

            <FlexGrid
              id={props.id}
              ref={updateRef}
              initialized={initGrid}
              isReadOnly={true}
              alternatingRowStep={0}
              itemsSource={gridRows}
              selectionMode="Row"
              headersVisibility={props.multiSelect ? "All" : "Column"}
              columns={gridHeader}
              autoSearch={true}
              allowSorting={AllowSorting.SingleColumn}
              multiSelect={false}
              stickyHeaders={false}
              keyActionTab={2}
              autoGenerateColumns={false}
              autoClipboard={true}
            >
              <FlexGridFilter
                // filterColumns={gridHeader.filter((e: any) => e.binding != "endcol").map((e: any) => e.binding)}
                ref={filterRef}
              />
            </FlexGrid>
            {
              props.paging ?
                <div className="grid-pager">
                  {
                    gridRef.current?.control?.collectionView ? (
                      <>
                        {
                          paging.filteredRows === 0 ? (
                            <h6 className="wj-count">
                              No Data Found
                            </h6>
                          ) : (
                            <h6 className="wj-count">
                              Showing {paging.startIndex} to {paging.endIndex} of {paging.filteredRows} entries {/* {paging.filteredRows != paging.totalRows ? `(filtered from ${paging.totalRows} total entries)` : null} */}
                            </h6>
                          )
                        }
                      </>
                    ) : null
                  }
                  <div className="wj-pagesize wj-control">
                    <div className="wj-template">
                      <div className="wj-input">
                        <div className="wj-input-group">
                          Show
                          <select className="wj-form-control" defaultValue={paging.pageSize} onChange={onPageSizeChange}>
                            {
                              pageSizes.map((x, i) => (
                                <option key={x} value={x}>{x}</option>
                              ))
                            }
                          </select>
                          Entries
                        </div>
                      </div>
                    </div>
                  </div>

                  {
                    props.serverPaging ?
                      <div className={classNames("wj-control wj-content wj-pager wj-collectionview-navigator wj-state-empty wj-state-readonly")} tabIndex={-1}>
                        <div className="wj-input-group">
                          <span wj-part="btn-first" className={classNames("wj-input-group-btn", { "wj-state-disabled": paging.pageIndex === 0 })}>
                            <button className="wj-btn wj-btn-default" tabIndex={-1} type="button" disabled={paging.pageIndex === 0} onClick={() => setPaging((x) => ({ ...x, pageIndex: 0 }))}>
                              <span className="wj-glyph-step-backward"></span>
                            </button>
                          </span>
                          <span wj-part="btn-prev" className={classNames("wj-input-group-btn", { "wj-state-disabled": paging.pageIndex === 0 })}>
                            <button className="wj-btn wj-btn-default" tabIndex={-1} type="button" disabled={paging.pageIndex === 0} onClick={() => setPaging((x) => ({ ...x, pageIndex: x.pageIndex - 1 }))}>
                              <span className="wj-glyph-left"></span>
                            </button>
                          </span>
                          <input wj-part="txt-curr" className="wj-form-control" readOnly={true} value={`Page ${paging.pageIndex + 1} of ${Math.ceil(paging.filteredRows / paging.pageSize)}`} />

                          <span wj-part="btn-next" className={classNames("wj-input-group-btn", { "wj-state-disabled": (paging.pageIndex + 1) * paging.pageSize >= paging.filteredRows })}>
                            <button className="wj-btn wj-btn-default" tabIndex={-1} type="button" disabled={(paging.pageIndex + 1) * paging.pageSize >= paging.filteredRows} onClick={() => setPaging((x) => ({ ...x, pageIndex: x.pageIndex + 1 }))}>
                              <span className="wj-glyph-right"></span>
                            </button>
                          </span>
                          <span wj-part="btn-last" className={classNames("wj-input-group-btn", { "wj-state-disabled": (paging.pageIndex + 1) * paging.pageSize >= paging.filteredRows })}>
                            <button className="wj-btn wj-btn-default" tabIndex={-1} type="button" disabled={(paging.pageIndex + 1) * paging.pageSize >= paging.filteredRows} onClick={() => setPaging((x) => ({ ...x, pageIndex: Math.floor(paging.filteredRows / paging.pageSize) }))}>
                              <span className="wj-glyph-step-forward"></span>
                            </button>
                          </span>
                        </div>
                      </div>
                      :
                      <CollectionViewNavigator
                        byPage={true}
                        headerFormat="Page {currentPage:n0} of {pageCount:n0}"
                        cv={gridRows}
                      />
                  }
                </div>
                : null
            }

          </div>
        </div>
        {props.multiSelect && props.multiSelectButton ? (
          <div className="modal-footer">
            <div>
              <Button variant="primary" onClick={selectMultiRow}>
                Select
              </Button>
            </div>
          </div>
        ) : null}
      </>
    );
  else return null;
});

TableHelpUI.defaultProps = {
  multiSelectButton: true,
  paging: true,
  allSearch: true,
}

export { TableHelpUI };

